import { useCallback, useEffect, useState } from 'react';

interface TranscendConsentData {
    purposes?: {
        Advertising?: boolean | 'Auto';
        SaleOfInfo?: boolean | 'Auto';
        [key: string]: boolean | 'Auto' | undefined;
    };
    [key: string]: any;
}

interface TranscendConsent {
    canStoreAttributionData: boolean;
    isOptInJurisdiction: boolean;
    isConsentDecisionMade: boolean;
    isInitialized: boolean;
}

declare global {
    interface Window {
        airgap?: {
            getRegimes?: () => Iterable<string>;
            getConsent?: () => TranscendConsentData;
            addEventListener?: (
                event: string,
                handler: (event: any) => void,
            ) => void;
            removeEventListener?: (
                event: string,
                handler: (event: any) => void,
            ) => void;
        };
    }
}

export const useTranscendConsent = (): TranscendConsent => {
    const [consentState, setConsentState] = useState<TranscendConsent>({
        canStoreAttributionData: false,
        isOptInJurisdiction: false,
        isConsentDecisionMade: false,
        isInitialized: false,
    });

    const checkJurisdiction = useCallback(() => {
        if (typeof window !== 'undefined' && window.airgap?.getRegimes) {
            const regimes = window.airgap.getRegimes();
            // Convert Iterable to array and get first value
            const regimesArray = Array.from(regimes);
            const defaultRegime =
                regimesArray.length > 0 ? regimesArray[0] : 'CPRA';

            // Opt-in regimes (GDPR, etc.)
            const optInRegimes = ['GDPR', 'LGPD', 'PIPEDA'];
            return optInRegimes.includes(defaultRegime);
        }

        // Fallback: assume opt-out
        return false;
    }, []);

    const checkConsentFromAirgap = useCallback(() => {
        try {
            if (typeof window !== 'undefined' && window.airgap?.getConsent) {
                const consentData = window.airgap.getConsent();
                const isOptInJurisdiction = checkJurisdiction();

                // Handle Advertising purpose for attribution data
                let canStoreAttributionData = false;
                const advertisingConsent = consentData.purposes?.Advertising;

                if (advertisingConsent === true) {
                    canStoreAttributionData = true;
                } else if (advertisingConsent === 'Auto') {
                    // For 'Auto' values, use regime to determine consent
                    canStoreAttributionData = !isOptInJurisdiction; // false for opt-in, true for opt-out
                } else if (advertisingConsent === false) {
                    canStoreAttributionData = false;
                } else {
                    // Fallback: default to true if no explicit consent
                    canStoreAttributionData = true;
                }

                // Determine if consent decision has been made
                // If Advertising is 'Auto', no explicit decision has been made
                // If Advertising is boolean (true/false), explicit decision has been made
                const isConsentDecisionMade =
                    advertisingConsent === true || advertisingConsent === false;

                return {
                    canStoreAttributionData,
                    isConsentDecisionMade,
                };
            }
        } catch (error) {
            console.warn('Failed to get consent from airgap:', error);
        }

        // Fallback: default to true if airgap fails
        return {
            canStoreAttributionData: true,
            isConsentDecisionMade: false,
        };
    }, [checkJurisdiction]);

    useEffect(() => {
        // Check initial consent state
        const initialConsent = checkConsentFromAirgap();
        const isOptInJurisdiction = checkJurisdiction();

        setConsentState({
            ...initialConsent,
            isOptInJurisdiction,
            isInitialized: true,
        });
    }, [checkConsentFromAirgap, checkJurisdiction]);

    return consentState;
};
